import { Injectable, inject } from '@angular/core';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { createEffect, ofType } from '@ngneat/effects';
import { SessionJobDatasetService } from '@data/session-job-dataset/session-job-dataset.service';
import { StudyFieldService } from '@data/study-field/study-field.service';
import { CourseGeneralService } from '@data/course-general/course-general.service';
import { SessionJobDatasetStore } from '@data/session-job-dataset/session-job-dataset.store';
import { StudyFieldStore } from '@data/study-field/study-field.store';
import { CourseGeneralStore } from '@data/course-general/course-general.store';
import { Observable, forkJoin, of } from 'rxjs';
import { SessionJobDatasetActions } from '@data/session-job-dataset/session-job-dataset.actions';
import { CustomBranchStore } from '@data/custom-branch/custom-branch.store';
import { CustomBranchService } from '@data/custom-branch/custom-branch.service';
import { CustomTopicService } from '@data/custom-topic/custom-topic.service';
import { CustomTopicStore } from '@data/custom-topic/custom-topic.store';
import { SessionJobDataset } from '@data/session-job-dataset/session-job-dataset.model';

@Injectable({ providedIn: 'root' })
export class SessionJobDatasetEffects {
  private datasetService = inject(SessionJobDatasetService);
  private studyFieldsService = inject(StudyFieldService);
  private courseGeneralService = inject(CourseGeneralService);
  private customBranchesService = inject(CustomBranchService);
  private customTopicsService = inject(CustomTopicService);
  private datasetStore = inject(SessionJobDatasetStore);
  private studyFieldsStore = inject(StudyFieldStore);
  private courseGeneralStore = inject(CourseGeneralStore);
  private customBranchesStore = inject(CustomBranchStore);
  private customTopicsStore = inject(CustomTopicStore);

  loadById = createEffect((actions) =>
    actions.pipe(
      ofType(SessionJobDatasetActions.loadById),
      switchMap(({ id, usePublic }) => {
        const source: Observable<SessionJobDataset | null | undefined> = id
          ? this.datasetService.getById(id, undefined, usePublic)
          : of(null);
        return source.pipe(
          map((dataset) => ({ dataset: dataset ?? null, usePublic: !!usePublic })),
          catchError(() =>
            of({ dataset: null as SessionJobDataset | null, usePublic: !!usePublic }),
          ),
        );
      }),
      tap(({ dataset }) => {
        if (!dataset) {
          this.datasetStore.setLoading(false);
          return;
        }
        this.studyFieldsStore.setLoading(true);
        this.courseGeneralStore.setLoading(true);
        this.customBranchesStore.setLoading(true);
        this.customTopicsStore.setLoading(true);
        this.datasetStore.add(dataset, { loading: false });
      }),
      switchMap(({ dataset, usePublic }) =>
        dataset ? this.loadOptions(dataset, usePublic) : of(null),
      ),
      tap((options) => {
        const [studyFields, coursesGeneral, customBranches, customTopics] = options ?? [];
        this.studyFieldsStore.set(studyFields ?? []);
        this.courseGeneralStore.set(coursesGeneral ?? []);
        this.customBranchesStore.set(customBranches ?? []);
        this.customTopicsStore.set(customTopics ?? []);
      }),
    ),
  );

  update = createEffect((actions) =>
    actions.pipe(
      ofType(SessionJobDatasetActions.update),
      switchMap(({ id, changes }) => this.datasetService.update(id, changes)),
    ),
  );

  /**
   * An anonymous exhibitor cannot read the per-collection list endpoints (they live under
   * `/private/*`, so AuthHttpInterceptor rejects them before they are sent) — the public form has
   * to resolve the dataset's options server-side instead, or every chip field stays empty.
   */
  private loadOptions(
    dataset: SessionJobDataset,
    usePublic: boolean,
  ): Observable<(any[] | undefined)[] | null> {
    if (usePublic) {
      return this.datasetService.getOptions(dataset._id).pipe(
        map((o) => [o.studyFields, o.coursesGeneral, o.branches, o.topics]),
        catchError(() => of(null)),
      );
    }
    return forkJoin([
      this.studyFieldsService.get({
        filter: JSON.stringify({ _id: { $in: dataset.availableStudyFields } }),
      }),
      this.courseGeneralService.get({
        filter: JSON.stringify({ _id: { $in: dataset.coursesGeneral } }),
      }),
      this.customBranchesService.get({
        filter: JSON.stringify({ _id: { $in: dataset.branches } }),
      }),
      this.customTopicsService.get({
        filter: JSON.stringify({ _id: { $in: dataset.topics } }),
      }),
    ]).pipe(catchError(() => of(null)));
  }
}
